import React, { useEffect, useMemo, useRef } from 'react';
import { DesignLayoutType, EventPageType } from '@graphql/generated-base';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { Box, ColumnsV2, Flex, styled } from '@withjoy/joykit';
import { Footer } from '../../components/Footer';
import { Body } from './components/Body';
import { brannanConfig, useBrannanDesign } from './LayoutBrannan.theme';
import { LazyWelcomeVideo } from '../../widgets/WelcomeVideo/WelcomeVideo.lazy';
import CoverPhoto from '../../components/CoverPhoto';
import { brannanFooterContainerStyles } from '@apps/guest/packages/layout-engine/components/Footer/Footer.styles';
import { useLayoutBrannanController } from './LayoutBrannan.controller';
import { findEventPageByPageSlug, getEventMenuPropsFromEvent } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import { Shell } from './components/Shell';
import { isInIframe } from '@shared/utils/isInIframe';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import WebsiteHeader from './components/WebsiteHeader/WebsiteHeader';
import { useWebsiteHeaderSettings } from '../hooks/useWebsiteHeaderSettings/useWebsiteHeaderSettings';
import type { PagesRefs } from '../LayoutAloha/LayoutAloha.types';
import { useAlohaScroll } from '../LayoutAloha/hooks/useAlohaScroll';
import { CoverPhotoWrapper } from './components/CoverPhotoWrapper';
import type { LayoutBrannanProps } from './LayoutBrannan';
import { PreloadImages } from '../../components/PreloadPageImages/PreloadImages';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import { StandaloneWebsiteHeader } from './components/StandaloneWebsiteHeader/StandaloneWebsiteHeader';
import { EVENT_PAGE_SLUGS } from '@apps/guest/shared/constants/eventPageSlugs';
import { useAccommodationPageChecks } from '@apps/guest/hooks/useAccommodationPageChecks';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';
import { useColorPaletteProvider } from '../ColorPaletteProvider/ColorPaletteProvider';
import { StickyNavBar } from '@apps/guest/packages/layout-engine/components/StickyNavBar';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useWelcomePagePhotoUrl } from '../hooks/useWelcomePagePhotoUrl';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useFilterMigratedLegacyPages } from '../../hooks/useFilterMigratedLegacyPages';
import { BrannanDesignProvider } from './BrannanDesignContext';
import { pxToRem } from '@withjoy/joykit/theme';
import FooterNav from '../../components/FooterNav/FooterNav';
import { SIMPLE_SCROLL_CONTAINER_ID } from '@apps/guest/routes/GuestSite/GuestSite.constants';
import { useQueryParamHelper } from '@shared/core/queryString';
import { AlohaMobileTopBarNav, AlohaMobileTopBarNavOffsetProvider } from '@apps/guest/packages/layout-engine/components/AlohaMobileTopBarNav';

const { componentTitleBanner } = brannanConfig.applicatorHtmlProps;

const ScrollableContainer = styled(Box)`
  width: 100%;
  height: 100vh;
  max-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 1;
`;

// Moments and RSVP are from mono repo which will be accessible via navbar but not scrolling
// Tidbits is already available as a part of page story
const PAGES_TO_BE_SKIPPED = [EventPageType.moments, EventPageType.rsvp, EventPageType.tidbits];

type BodySectionProps = Readonly<{
  page: EventPageFragment;
  event: LayoutBrannanProps['event'];
  eventHandle: string;
  websiteHeader: ReturnType<typeof useWebsiteHeaderSettings>['websiteHeader'];
  isNativeAccommodations: boolean;
  isWelcomePage?: boolean;
  isCustomWelcomePage?: boolean;
  welcomePhotoUrl?: string | null;
}>;

const BodySection = ({
  page,
  event,
  eventHandle,
  websiteHeader,
  isNativeAccommodations,
  isWelcomePage = false,
  isCustomWelcomePage = false,
  welcomePhotoUrl,
}: BodySectionProps) => {
  const shouldHideColumn = useMemo(() => {
    return isNativeAccommodations && !page?.subTitle;
  }, [isNativeAccommodations, page?.subTitle]);

  return (
    <Body
      data-testid={`layout-body-${page.pageSlug}`}
      marginTop={[0, null, 9]}
      page={page}
      event={event}
      eventHandle={eventHandle}
      isSinglePage={true}
      isWelcomePage={isWelcomePage}
      isCustomWelcomePage={isCustomWelcomePage}
      photo={
        isWelcomePage && event.video ? (
          <ColumnsV2.Column
            style={page.type === EventPageType.custom ? { display: shouldHideColumn ? 'none' : 'block' } : {}}
            span={[12, 10]}
            offset={[0, 1]}
          >
            <LazyWelcomeVideo
              video={event.video}
              thumbnailUrl={event.video.thumbnailUrl || welcomePhotoUrl}
            />
          </ColumnsV2.Column>
        ) : isWelcomePage && websiteHeader ? null : (
          // Only hide page photo for native welcome page when websiteHeader is present
          // Custom welcome pages should show their cover photo
          <CoverPhotoWrapper
            renderFullColumn={page.photoPresentation?.presentationLayout !== 'center'}
            isCustomPage={page.type === EventPageType.custom}
            isNativeAccommodations={isNativeAccommodations}
            subTitle={page.subTitle}
          >
            <CoverPhoto
              websiteLayoutType={DesignLayoutType.brannan}
              photo={page.photoV2}
              pageId={page.id}
              pageType={page.type}
              pageSlug={page.pageSlug}
              photoPresentation={page.photoPresentation}
            />
          </CoverPhotoWrapper>
        )
      }
    />
  );
};

interface PageSectionProps
  extends Readonly<{
    page: EventPageFragment;
    event: LayoutBrannanProps['event'];
    eventHandle: string;
    websiteHeader: ReturnType<typeof useWebsiteHeaderSettings>['websiteHeader'];
    pagesRef: Record<string, PagesRefs>;
    isWelcomePage: boolean;
    isCustomWelcomePage: boolean;
    welcomePhotoUrl?: string | null;
  }> {}

const PageSectionAccommodationsPage = React.memo<PageSectionProps>(({ page, event, eventHandle, websiteHeader, pagesRef, welcomePhotoUrl }) => {
  const { isNativeAccommodationPage } = useAccommodationPageChecks(page.pageSlug);

  return (
    <Box
      paddingTop={{ _: 0, sm2: pxToRem(96) }}
      paddingBottom={{ _: 10, sm2: 0 }}
      key={page.id}
      ref={ref => (pagesRef[page.id] = { ref, page })}
    >
      <Flex
        data-testid={'page-custom'}
        justifyContent="center"
        alignItems="center"
        margin={'0 auto'}
      >
        <BodySection
          page={page}
          event={event}
          eventHandle={eventHandle}
          websiteHeader={websiteHeader}
          isNativeAccommodations={isNativeAccommodationPage}
          isWelcomePage={false}
          welcomePhotoUrl={welcomePhotoUrl}
        />
      </Flex>
    </Box>
  );
});

const PageSectionCustomPage = React.memo<PageSectionProps>(({ page, event, eventHandle, websiteHeader, pagesRef, isWelcomePage, isCustomWelcomePage, welcomePhotoUrl }) => {
  const { isNativeAccommodationPage } = useAccommodationPageChecks(page.pageSlug);

  return (
    <Box
      paddingTop={{ _: isWelcomePage ? 10 : 0, sm2: pxToRem(96) }}
      paddingBottom={{ _: 10, sm2: 0 }}
      key={page.id}
      ref={ref => (pagesRef[page.id] = { ref, page })}
    >
      <BodySection
        page={page}
        event={event}
        eventHandle={eventHandle}
        websiteHeader={websiteHeader}
        isNativeAccommodations={isNativeAccommodationPage}
        isWelcomePage={isWelcomePage}
        isCustomWelcomePage={isCustomWelcomePage}
        welcomePhotoUrl={welcomePhotoUrl}
      />
    </Box>
  );
});

const PageSection = React.memo<PageSectionProps>(props => {
  const { page, event, eventHandle, websiteHeader, pagesRef, isWelcomePage, isCustomWelcomePage, welcomePhotoUrl } = props;
  const { isAccommodationsPage, isNativeAccommodationPage } = useAccommodationPageChecks(page.pageSlug);

  if (page.type === EventPageType.custom) {
    if (isAccommodationsPage) {
      return <PageSectionAccommodationsPage {...props} />;
    }
    return <PageSectionCustomPage {...props} />;
  }

  return (
    <Box
      paddingTop={{ _: isWelcomePage ? 10 : 0, sm2: pxToRem(96) }}
      paddingBottom={{ _: 10, sm2: 0 }}
      ref={ref => (pagesRef[page.id] = { ref, page })}
    >
      <BodySection
        page={page}
        event={event}
        eventHandle={eventHandle}
        websiteHeader={websiteHeader}
        isNativeAccommodations={isNativeAccommodationPage}
        isWelcomePage={isWelcomePage}
        isCustomWelcomePage={isCustomWelcomePage}
        welcomePhotoUrl={welcomePhotoUrl}
      />
    </Box>
  );
});

const PagesSection = React.memo<{
  filteredPages: EventPageFragment[];
  event: LayoutBrannanProps['event'];
  eventHandle: string;
  websiteHeader: ReturnType<typeof useWebsiteHeaderSettings>['websiteHeader'];
  pagesRef: Record<string, PagesRefs>;
  hideNav?: boolean;
  welcomePhotoUrl?: string | null;
}>(({ filteredPages, hideNav, ...restProps }) => {
  const { checkIsWelcomePage } = useWelcomePage();

  return (
    <>
      {filteredPages.map(page => {
        const { isWelcomePage, isCustomWelcomePage } = checkIsWelcomePage(page.pageSlug, page.type);

        if (hideNav && isWelcomePage) {
          return null;
        }

        return (
          <PageSection
            key={page.id}
            page={page}
            isWelcomePage={isWelcomePage}
            isCustomWelcomePage={isCustomWelcomePage}
            {...restProps}
          />
        );
      })}
    </>
  );
});

const LayoutBrannanSinglePage: React.FC<LayoutBrannanProps> = ({ eventHandle, event, setLoadingTheme, setIsLastPageVisible, setIsPastFirstPage, hideNav }) => {
  const { info, pages, eventDesign } = event;
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobileOrTablet = useIsMobileOrTablet();
  const { getValueString } = useQueryParamHelper();
  const pagesRef = useMemo(() => ({}), []) as Record<string, PagesRefs>;

  const [isSm2orLarge] = useResponsive({ values: { _: false, sm2: true } });
  const [isGuestMobileFooterNavEnabled] = useIsFeatureEnabled('guestMobileFooterNavEnabled', { skip: isSm2orLarge });

  // Filter out legacy pages that have been migrated to custom pages when feature flag is enabled
  const { filteredPages: pagesWithoutMigratedLegacy } = useFilterMigratedLegacyPages(pages);

  const filteredPages = useMemo(
    () => pagesWithoutMigratedLegacy.filter(page => page.type !== EventPageType.tidbits && !PAGES_TO_BE_SKIPPED.includes(page.type)),
    [pagesWithoutMigratedLegacy],
  );
  const { checkIsWelcomePage } = useWelcomePage();
  const scrollPages = useMemo(() => {
    if (!hideNav) return filteredPages;
    return filteredPages.filter(page => !checkIsWelcomePage(page.pageSlug, page.type).isWelcomePage);
  }, [filteredPages, hideNav, checkIsWelcomePage]);

  useAlohaScroll(scrollPages, pagesRef, containerRef.current, setIsLastPageVisible, setIsPastFirstPage);
  const isPreviewing = isInIframe();
  // Admin preview (joy's own preview iframe) sets ?preview=true — distinct from other
  // iframe contexts like Carbon embedding or custom domains where the nav should remain sticky.
  const isAdminPreview = isPreviewing && getValueString('preview') === 'true';

  const { handleMenuClicked, handleFooterButtonClicked, handleEditEventClicked } = useLayoutBrannanController(pagesWithoutMigratedLegacy);
  const { currentPageSlug, isCustomPageSlug, isUrlBasedCustomPageSlug } = useCurrentPageSlug(eventHandle);
  const { isNativeAccommodationPage } = useAccommodationPageChecks(currentPageSlug);
  const { websiteHeader } = useWebsiteHeaderSettings({
    eventHandle,
    targetWebsiteLayout: 'brannan',
  });
  const welcomePhotoUrl = useWelcomePagePhotoUrl({ event, headerPhoto: websiteHeader?.headerPhoto });

  const { isSmallScreen, reapplyDesign, loading } = useBrannanDesign(eventHandle, eventDesign);
  const { pageColor, pageFontColor } = useColorPaletteProvider();

  // Create eventProps with filtered pages for navigation menus (including hamburger menu)
  const eventMenuProps = useMemo(() => ({ ...getEventMenuPropsFromEvent(event), pages: pagesWithoutMigratedLegacy }), [event, pagesWithoutMigratedLegacy]);

  const headerProps = useMemo(() => {
    // Fallback to a type-agnostic lookup when the flag-based custom lookup misses a page
    // that hasn't been migrated yet. See LayoutBrannan.tsx for full explanation.
    const currentPage = currentPageSlug
      ? (findEventPageByPageSlug(pagesWithoutMigratedLegacy, currentPageSlug, isCustomPageSlug, isUrlBasedCustomPageSlug) ??
        (isCustomPageSlug ? findEventPageByPageSlug(pagesWithoutMigratedLegacy, currentPageSlug) : null))
      : null;

    return {
      page: currentPage?.type,
      pageSlug: currentPage?.pageSlug,
      pageId: currentPage?.id,
      isCustomPage: currentPage?.type === EventPageType.custom,
    };
  }, [currentPageSlug, isCustomPageSlug, isUrlBasedCustomPageSlug, pagesWithoutMigratedLegacy]);

  useEffect(() => {
    setLoadingTheme?.(loading);
  }, [loading, setLoadingTheme]);

  useEffect(() => {
    if (isPreviewing) {
      sendMessageToParentWindow({ action: 'requestIframeScaleValue', source: 'joyPreview' });
    }
  }, [isPreviewing, headerProps.pageSlug]);

  return (
    <AlohaMobileTopBarNavOffsetProvider>
      <BrannanDesignProvider value={{ reapplyDesign }}>
        <ScrollableContainer
          ref={containerRef}
          id={SIMPLE_SCROLL_CONTAINER_ID}
        >
          {!hideNav && (
            <AlohaMobileTopBarNav
              eventHandle={eventHandle}
              // Brannan single-page strips `tidbits` + `PAGES_TO_BE_SKIPPED` (moments, rsvp, tidbits)
              // from the rendered page stack; the nav must use the same filtered set so it
              // never advertises a section the layout doesn't actually render.
              pages={filteredPages}
            />
          )}
          {!hideNav && !isSmallScreen && !isAdminPreview && (
            <StickyNavBar
              scrollableContainerRef={containerRef}
              pages={pagesWithoutMigratedLegacy}
              eventHandle={eventHandle}
              handleMenuClicked={handleMenuClicked}
              eventProps={eventMenuProps}
              pageColor={pageColor}
              textColor={pageFontColor}
            />
          )}
          {websiteHeader && !hideNav && (
            <StandaloneWebsiteHeader
              websiteHeader={websiteHeader}
              navBarVariant={isSmallScreen ? 'menu' : 'inline'}
              pages={pagesWithoutMigratedLegacy}
              currentPageType={EventPageType.welcome}
              eventHandle={eventHandle}
              handleMenuClicked={handleMenuClicked}
              eventProps={eventMenuProps}
              pageSlug={EVENT_PAGE_SLUGS.Welcome}
              eventDesign={eventDesign}
            />
          )}
          <Shell
            eventHandle={eventHandle}
            eventDesign={event.eventDesign}
            isCustomPage={headerProps.isCustomPage}
          >
            <PreloadImages
              pages={filteredPages}
              eventHandle={eventHandle}
              headerPhoto={websiteHeader?.headerPhoto}
            />
            <Box paddingTop={isMobileOrTablet ? 0 : '6rem'}>
              {!isNativeAccommodationPage && !hideNav && (
                <WebsiteHeader
                  data-testid={'layout-header'}
                  websiteHeader={websiteHeader}
                  applicatorProp={componentTitleBanner.prop}
                  navBarVariant={isSmallScreen ? 'menu' : 'inline'}
                  eventHandle={eventHandle}
                  pages={pagesWithoutMigratedLegacy}
                  info={info}
                  handleMenuClicked={handleMenuClicked}
                  handleEditEventClicked={handleEditEventClicked}
                  eventProps={eventMenuProps}
                  page={headerProps.page}
                  pageSlug={headerProps.pageSlug}
                  pageId={headerProps.pageId}
                  eventDisplayName={info?.eventDisplayName}
                />
              )}
            </Box>
            <PagesSection
              filteredPages={filteredPages}
              event={event}
              eventHandle={eventHandle}
              websiteHeader={websiteHeader}
              pagesRef={pagesRef}
              hideNav={hideNav}
              welcomePhotoUrl={welcomePhotoUrl}
            />
            <Flex __css={brannanFooterContainerStyles}>
              {/* FooterNav is only visible on mobile */}
              {isGuestMobileFooterNavEnabled && !hideNav && (
                <Flex
                  width="100%"
                  display={{ _: 'flex', sm2: 'none' }}
                >
                  <FooterNav
                    pages={filteredPages}
                    eventHandle={eventHandle}
                    eventDisplayName={info?.eventDisplayName}
                    handleMenuClicked={handleMenuClicked}
                  />
                </Flex>
              )}
              <Footer
                onButtonClicked={handleFooterButtonClicked}
                data-testid={'layout-footer'}
              />
            </Flex>
          </Shell>
        </ScrollableContainer>
      </BrannanDesignProvider>
    </AlohaMobileTopBarNavOffsetProvider>
  );
};

LayoutBrannanSinglePage.displayName = 'LayoutBrannanSinglePage';

export { LayoutBrannanSinglePage };
